import type { UseEnquiryProps } from '~/modules/enquiry/hooks/use-enquiry';

/**
 * Brand-pane messaging for the enquiry gate, keyed by what is being enquired
 * about. This copy is owned together with Marketing — tweak the strings here;
 * nothing else in the gate needs to change.
 */
export interface EnquiryGateStatementContext {
    professionalTitle: string;
    /**
     * The project the enquiry relates to when the enquiry itself targets a
     * professional (project-page surfaces track with itemType 'professional'
     * but carry the project here).
     */
    relatedItem?: UseEnquiryProps['relatedItem'];
}

type StatementBuilder = (ctx: EnquiryGateStatementContext) => string;

/**
 * The statement names the professional and stops there. The item is already the
 * pane's imagery and the modal's own title, so naming it a third time only made
 * the heading run to four lines on long product names.
 */
const STATEMENTS: Record<UseEnquiryProps['itemType'], StatementBuilder> = {
    product: ({ professionalTitle }) =>
        `Send a direct message to ${professionalTitle}`,
    professional: ({ professionalTitle }) =>
        `Send a direct message to ${professionalTitle}`,
    project: ({ professionalTitle }) =>
        `Send a message to ${professionalTitle}`,
    article: ({ professionalTitle }) =>
        `Send a direct message to ${professionalTitle}`,
};

export const getEnquiryGateStatement = (
    itemType: UseEnquiryProps['itemType'],
    ctx: EnquiryGateStatementContext,
): string => {
    // A professional enquiry made from a project page reads as a project
    // enquiry, and takes the softer opening.
    const intent =
        itemType !== 'product' && ctx.relatedItem?.type === 'project'
            ? 'project'
            : itemType;
    return (STATEMENTS[intent] ?? STATEMENTS.professional)(ctx);
};
